/***
*
*   SETTINGS PANEL
*   Manage trial, upgrade/downgrade, and discount settings
*
**********/

import { useState, useEffect } from 'react';
import { Card, Button } from 'components/lib';
import Style from './bundlePackaging.tailwind.js';

export function SettingsPanel({ 
  trialSettings, 
  upgradeSettings, 
  discounts, 
  onSaveTrialSettings, 
  onSaveUpgradeSettings, 
  onSaveDiscounts,
  loading 
}) {
  const [trialForm, setTrialForm] = useState({
    trial_length_days: 14,
    conversion_rules: {
      auto_downgrade_to: 'free',
      grace_period_days: 7,
      notify_before_days: [7, 3, 1],
    },
  });

  const [upgradeForm, setUpgradeForm] = useState({
    proration_enabled: true,
    grace_window_days: 3,
    safe_deactivation: true,
  });

  const [discountForm, setDiscountForm] = useState({
    annual_billing_percent: 20,
    volume_discount_threshold: 500,
  });

  useEffect(() => {
    if (trialSettings) setTrialForm(trialSettings);
    if (upgradeSettings) setUpgradeForm(upgradeSettings);
    if (discounts) setDiscountForm(discounts);
  }, [trialSettings, upgradeSettings, discounts]);

  return (
    <div className="space-y-6">
      {/* Trial Settings */}
      <Card>
        <div className={Style.settingsSection}>
          <h3 className={Style.settingsHeader}>Trial Settings</h3>
          <div className={Style.settingsGrid}>
            <div className={Style.settingsCard}>
              <label className={Style.settingsLabel}>Trial Length (Days)</label>
              <input
                type="number"
                className={Style.settingsInput}
                value={trialForm.trial_length_days}
                onChange={(e) => setTrialForm(prev => ({ 
                  ...prev, 
                  trial_length_days: parseInt(e.target.value) 
                }))}
                min="1"
              />
            </div>
            <div className={Style.settingsCard}>
              <label className={Style.settingsLabel}>Auto Downgrade To</label>
              <select
                className={Style.settingsInput}
                value={trialForm.conversion_rules?.auto_downgrade_to || 'free'}
                onChange={(e) => setTrialForm(prev => ({ 
                  ...prev, 
                  conversion_rules: { 
                    ...prev.conversion_rules, 
                    auto_downgrade_to: e.target.value 
                  } 
                }))}
              >
                <option value="free">Free Plan</option>
                <option value="suspend">Suspend Account</option>
              </select>
            </div>
            <div className={Style.settingsCard}>
              <label className={Style.settingsLabel}>Grace Period (Days)</label>
              <input
                type="number"
                className={Style.settingsInput}
                value={trialForm.conversion_rules?.grace_period_days || 7}
                onChange={(e) => setTrialForm(prev => ({ 
                  ...prev, 
                  conversion_rules: { 
                    ...prev.conversion_rules, 
                    grace_period_days: parseInt(e.target.value) 
                  } 
                }))}
                min="0"
              />
            </div>
          </div>
          <div className="mt-4">
            <Button
              text="Save Trial Settings"
              color="green"
              loading={loading}
              action={() => onSaveTrialSettings(trialForm)}
            />
          </div>
        </div>
      </Card>

      {/* Upgrade/Downgrade Settings */}
      <Card>
        <div className={Style.settingsSection}>
          <h3 className={Style.settingsHeader}>Upgrade/Downgrade Settings</h3>
          <div className={Style.settingsGrid}>
            <div className={Style.settingsCard}>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={upgradeForm.proration_enabled}
                  onChange={(e) => setUpgradeForm(prev => ({ 
                    ...prev, 
                    proration_enabled: e.target.checked 
                  }))}
                />
                <span className={Style.settingsLabel}>Enable Proration</span>
              </label>
            </div>
            <div className={Style.settingsCard}>
              <label className={Style.settingsLabel}>Grace Window (Days)</label>
              <input
                type="number"
                className={Style.settingsInput}
                value={upgradeForm.grace_window_days}
                onChange={(e) => setUpgradeForm(prev => ({ 
                  ...prev, 
                  grace_window_days: parseInt(e.target.value) 
                }))}
                min="0"
              />
            </div>
            <div className={Style.settingsCard}>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={upgradeForm.safe_deactivation}
                  onChange={(e) => setUpgradeForm(prev => ({ 
                    ...prev, 
                    safe_deactivation: e.target.checked 
                  }))}
                />
                <span className={Style.settingsLabel}>Safe Deactivation</span>
              </label>
            </div>
          </div>
          <div className="mt-4">
            <Button
              text="Save Upgrade Settings"
              color="green"
              loading={loading}
              action={() => onSaveUpgradeSettings(upgradeForm)}
            />
          </div>
        </div>
      </Card>

      {/* Discount Settings */}
      <Card>
        <div className={Style.settingsSection}>
          <h3 className={Style.settingsHeader}>Discount Settings</h3>
          <div className={Style.settingsGrid}>
            <div className={Style.settingsCard}>
              <label className={Style.settingsLabel}>Annual Billing Discount (%)</label>
              <input
                type="number"
                className={Style.settingsInput}
                value={discountForm.annual_billing_percent}
                onChange={(e) => setDiscountForm(prev => ({ 
                  ...prev, 
                  annual_billing_percent: parseInt(e.target.value) 
                }))}
                min="0"
                max="100"
              />
            </div>
            <div className={Style.settingsCard}>
              <label className={Style.settingsLabel}>Volume Discount Threshold</label>
              <input
                type="number"
                className={Style.settingsInput}
                value={discountForm.volume_discount_threshold}
                onChange={(e) => setDiscountForm(prev => ({ 
                  ...prev, 
                  volume_discount_threshold: parseInt(e.target.value) 
                }))}
                min="0"
              />
            </div>
          </div>
          <div className="mt-4">
            <Button
              text="Save Discounts"
              color="green"
              loading={loading}
              action={() => onSaveDiscounts(discountForm)}
            />
          </div>
        </div>
      </Card>
    </div>
  );
}